import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  listAccountDeletions,
  DeletionListParams,
  DeletionRequestList,
} from '../../services/accountDeletionAdminService';
import { RefreshIcon, SearchIcon } from '../../components/common/Icons';
import {
  APPROVAL_LABELS,
  EXECUTION_LABELS,
  HOLD_LABELS,
  LEDGER_STATUS_OPTIONS,
  OUTSTANDING_LABELS,
  STATUS_LABELS,
  STATUS_TONES,
  label,
} from './accountDeletionLabels';
import '../../styles/AuditTrailsPage.css';
import '../../styles/AccountDeletionsPage.css';

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy HH:mm');
  } catch {
    return '—';
  }
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`account-deletions__status account-deletions__status--${STATUS_TONES[status] || 'neutral'}`}>
    {label(STATUS_LABELS, status)}
  </span>
);

/** Shown on every Account Deletions screen. */
export const ReadOnlyNotice: React.FC = () => (
  <div className="account-deletions__notice" role="note">
    <strong>Read-only view.</strong> Approving, executing, cancelling or otherwise changing an account
    deletion is not possible here. Those steps are carried out only through the separately authorized
    two-person operator process.
  </div>
);

const errorMessage = (err: any): string => {
  const status = err?.response?.status;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have access to account deletions.';
  if (status === 429) return 'Too many requests. Please wait a moment before refreshing.';
  return 'Account deletion requests could not be loaded.';
};

const DEFAULT_FILTERS: DeletionListParams = { page: 1, limit: 20, status: '', userType: '' };

const AccountDeletionsPage: React.FC = () => {
  const [result, setResult] = useState<DeletionRequestList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DeletionListParams>(DEFAULT_FILTERS);
  const [applied, setApplied] = useState<DeletionListParams>(DEFAULT_FILTERS);
  const requestSeq = useRef(0);

  // keepRows: only a manual refresh of the same filters keeps the current rows
  // on screen; a filter or page change clears them at once.
  const load = useCallback(async (params: DeletionListParams, keepRows = false) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    if (!keepRows) setResult(null);
    try {
      const data = await listAccountDeletions(params);
      if (seq === requestSeq.current) setResult(data);
    } catch (err) {
      if (seq === requestSeq.current) {
        setResult(null);
        setError(errorMessage(err));
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  const apply = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    setApplied(next);
  };

  const goToPage = (page: number) => setApplied((prev) => ({ ...prev, page }));

  const items = result?.items || [];
  const pagination = result?.pagination;
  const process = result?.process;

  return (
    <div className="audit-trails account-deletions">
      <header className="audit-trails__header">
        <div>
          <h1 className="audit-trails__title">Account Deletions</h1>
          <p className="audit-trails__subtitle">Deletion requests, holds and completion status</p>
        </div>
        <button
          type="button"
          className="audit-trails__refresh-btn"
          onClick={() => load(applied, true)}
          disabled={loading}
        >
          <RefreshIcon />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      <ReadOnlyNotice />

      {process && (
        <section className="account-deletions__process" aria-label="Deletion process status">
          <div>
            <span className="account-deletions__process-label">Production deletion policy</span>
            <span
              className={`account-deletions__status account-deletions__status--${
                process.productionPolicyActive ? 'info' : 'neutral'
              }`}
            >
              {process.productionPolicyActive ? 'Approved' : 'Inactive'}
            </span>
          </div>
          <div>
            <span className="account-deletions__process-label">Emergency stop</span>
            <span>
              {process.emergencyStop === true ? 'Active' : process.emergencyStop === false ? 'Off' : 'Unknown'}
            </span>
          </div>
          <div>
            <span className="account-deletions__process-label">Cancellation window</span>
            <span>{process.cancellationDays} days</span>
          </div>
          <div>
            <span className="account-deletions__process-label">Completion target</span>
            <span>
              {process.completionTargetDays
                ? `${process.completionTargetDays} days after the window`
                : 'Not approved – no completion deadline is stated'}
            </span>
          </div>
          <div className="account-deletions__process-note">
            Execution is decided only by the separately authorized operator process. Stripe and other provider
            checks are not performed here.
          </div>
          {result && result.summary.openRequestsWithoutLedger > 0 && (
            <div className="account-deletions__warning" role="status">
              {result.summary.openRequestsWithoutLedger} open request(s) have no tracking record yet and are not
              listed below.
            </div>
          )}
          {result && result.summary.openRequestsWithoutLedgerCapped && (
            <div className="account-deletions__warning" role="status">
              More than 1,000 open requests exist; only the first 1,000 were checked for a missing tracking record.
            </div>
          )}
        </section>
      )}

      <section className="audit-trails__filters">
        <div className="audit-trails__filters-row">
          <label className="account-deletions__field">
            <span>Status</span>
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as DeletionListParams['status'] }))}
              className="audit-trails__select"
            >
              {LEDGER_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="account-deletions__field">
            <span>Account type</span>
            <select
              value={filters.userType}
              onChange={(e) =>
                setFilters((f) => ({ ...f, userType: e.target.value as DeletionListParams['userType'] }))
              }
              className="audit-trails__select"
            >
              <option value="">All account types</option>
              <option value="client">Client</option>
              <option value="stylist">Stylist</option>
            </select>
          </label>
          <button type="button" className="audit-trails__apply-btn" onClick={apply}>
            <SearchIcon />
            Apply
          </button>
        </div>
      </section>

      <div className="audit-trails__table-wrap">
        {loading && !result ? (
          <div className="audit-trails__loading" role="status">
            Loading account deletion requests…
          </div>
        ) : error ? (
          <div className="account-deletions__error" role="alert">
            <p>{error}</p>
            <button type="button" className="audit-trails__page-btn" onClick={() => load(applied, true)}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="account-deletions__scroll" data-testid="account-deletions-scroll">
              <table className="audit-trails__table account-deletions__table">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Account</th>
                    <th>Requested</th>
                    <th>Cancellation deadline</th>
                    <th>Status</th>
                    <th>Approval</th>
                    <th>Execution</th>
                    <th>Completion</th>
                    <th>Holds / outstanding</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="audit-trails__empty">
                        No account deletion requests match these filters.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => {
                      const outstanding = item.status === 'completed' ? item.completion.outstanding : [];
                      return (
                        <tr key={item.id} className="audit-trails__row">
                          <td>
                            <Link to={`/admin/account-deletions/${item.id}`} className="account-deletions__link">
                              <code>{item.id}</code>
                            </Link>
                          </td>
                          <td className="account-deletions__account">
                            <code className="account-deletions__muted">{item.userId}</code>
                            <span className="audit-trails__target-type">{item.userType || '—'}</span>
                          </td>
                          <td className="audit-trails__time">{formatDate(item.requestedAt)}</td>
                          <td className="audit-trails__time">
                            {item.status === 'cancelled' ? (
                              <span className="account-deletions__muted">Cancelled {formatDate(item.cancelledAt)}</span>
                            ) : (
                              formatDate(item.cancellationDeadline)
                            )}
                          </td>
                          <td>
                            <StatusBadge status={item.displayStatus} />
                          </td>
                          <td>{label(APPROVAL_LABELS, item.approval.state)}</td>
                          <td>
                            {label(EXECUTION_LABELS, item.execution.state)}
                            {item.execution.state !== 'not_started' && (
                              <span className="account-deletions__muted">
                                {' '}
                                ({item.execution.stepsCompleted}/{item.execution.stepsTotal})
                              </span>
                            )}
                          </td>
                          <td>
                            {item.status !== 'completed'
                              ? '—'
                              : item.completion.fullyComplete
                                ? 'Fully verified'
                                : 'Not verified'}
                          </td>
                          <td className="account-deletions__holds">
                            {item.holds.length === 0 && outstanding.length === 0 ? (
                              <span className="account-deletions__muted">None recorded</span>
                            ) : (
                              <ul className="account-deletions__chips">
                                {item.holds.map((code) => (
                                  <li key={`h-${code}`} className="account-deletions__chip account-deletions__chip--hold">
                                    {label(HOLD_LABELS, code)}
                                  </li>
                                ))}
                                {outstanding.map((code) => (
                                  <li key={`o-${code}`} className="account-deletions__chip">
                                    {label(OUTSTANDING_LABELS, code)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td className="audit-trails__time">{formatDate(item.updatedAt)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="audit-trails__pagination">
                <button
                  type="button"
                  className="audit-trails__page-btn"
                  disabled={pagination.page <= 1 || loading}
                  onClick={() => goToPage(pagination.page - 1)}
                >
                  Previous
                </button>
                <span className="audit-trails__page-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <button
                  type="button"
                  className="audit-trails__page-btn"
                  disabled={pagination.page >= pagination.totalPages || loading}
                  onClick={() => goToPage(pagination.page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountDeletionsPage;
